/** Battery layout editor (plan §8.4): section and battery labels, section
 * assignment, position, confirmed flag and the per-section clock offset. */
import type { LayoutBattery, LayoutSection, SiteLayout } from "../api/types";

/** Layout content without its provenance, for "is this the saved layout?" checks. */
export function layoutFingerprint(layout: SiteLayout): string {
  return JSON.stringify({
    sections: layout.sections.map((section) => ({
      id: section.id,
      label: section.label,
      logger_sn: section.logger_sn ?? null,
      clock_offset_minutes: section.clock_offset_minutes || 0,
      batteries: section.batteries.map((b) => [b.serial, b.position, b.label, b.position_confirmed]),
    })),
    unassigned: [...layout.unassigned].sort(),
  });
}

export function hasClockOffsets(layout: SiteLayout): boolean {
  return layout.sections.some((section) => (section.clock_offset_minutes || 0) !== 0);
}

function nextSectionId(layout: SiteLayout): string {
  const numbers = layout.sections.map((section) => Number(section.id)).filter((value) => Number.isInteger(value));
  return String((numbers.length ? Math.max(...numbers) : 0) + 1);
}

function nextPosition(section: LayoutSection): number {
  return section.batteries.reduce((highest, battery) => Math.max(highest, battery.position), 0) + 1;
}

export function LayoutEditor({ layout, onChange, disabled }: { layout: SiteLayout; onChange: (next: SiteLayout) => void; disabled?: boolean }) {
  const patchSection = (id: string, patch: Partial<LayoutSection>) =>
    onChange({ ...layout, sections: layout.sections.map((section) => (section.id === id ? { ...section, ...patch } : section)) });

  const patchBattery = (sectionId: string, serial: string, patch: Partial<LayoutBattery>) =>
    onChange({
      ...layout,
      sections: layout.sections.map((section) =>
        section.id === sectionId
          ? { ...section, batteries: section.batteries.map((battery) => (battery.serial === serial ? { ...battery, ...patch } : battery)) }
          : section,
      ),
    });

  /** Move a battery to another section (or to "unassigned" with an empty target). */
  const move = (serial: string, from: string | null, to: string) => {
    if (from === to) return;
    const sections = layout.sections.map((section) => {
      if (section.id === from) return { ...section, batteries: section.batteries.filter((battery) => battery.serial !== serial) };
      if (section.id === to) {
        const position = nextPosition(section);
        // The position inside the new section is not known from the data: unconfirmed.
        return {
          ...section,
          batteries: [...section.batteries, { serial, position, label: `ESS ${section.id}.${position} (${serial})`, position_confirmed: false }],
        };
      }
      return section;
    });
    const unassigned = to === "" ? [...layout.unassigned, serial] : layout.unassigned.filter((item) => item !== serial);
    onChange({ ...layout, sections, unassigned });
  };

  const addSection = () => {
    const id = nextSectionId(layout);
    onChange({ ...layout, sections: [...layout.sections, { id, label: `Section ${id}`, logger_sn: null, clock_offset_minutes: 0, batteries: [] }] });
  };

  const sectionSelect = (serial: string, current: string | null) => (
    <select
      className="input"
      style={{ minHeight: 34, padding: "0.2rem 0.4rem", width: "auto" }}
      aria-label={`Section of ${serial}`}
      value={current ?? ""}
      disabled={disabled}
      onChange={(event) => move(serial, current, event.target.value)}
    >
      <option value="">Unassigned</option>
      {layout.sections.map((section) => (
        <option key={section.id} value={section.id}>
          {section.label}
        </option>
      ))}
    </select>
  );

  return (
    <div className="stack">
      <div className="layout-grid">
        {layout.sections.map((section) => (
          <div className="layout-section" key={section.id}>
            <header>
              <input
                className="input"
                aria-label={`Label of section ${section.id}`}
                value={section.label}
                disabled={disabled}
                maxLength={60}
                onChange={(event) => patchSection(section.id, { label: event.target.value })}
              />
              {section.batteries.length === 0 && !disabled && (
                <button
                  type="button"
                  className="btn small ghost"
                  onClick={() => onChange({ ...layout, sections: layout.sections.filter((item) => item.id !== section.id) })}
                >
                  Remove
                </button>
              )}
            </header>
            <div className="row small muted" style={{ marginBottom: "0.5rem" }}>
              <span className="mono">Logger {section.logger_sn ?? "—"}</span>
              <label className="row" style={{ gap: "0.35rem" }}>
                Clock offset (min)
                <input
                  className="input"
                  style={{ width: 86, minHeight: 34, padding: "0.2rem 0.4rem" }}
                  type="number"
                  step={15}
                  min={-1440}
                  max={1440}
                  aria-label={`Clock offset of ${section.label} in minutes`}
                  value={section.clock_offset_minutes}
                  disabled={disabled}
                  onChange={(event) => patchSection(section.id, { clock_offset_minutes: Math.trunc(Number(event.target.value) || 0) })}
                />
              </label>
            </div>
            {section.batteries.length === 0 && <p className="muted small">No batteries.</p>}
            {[...section.batteries]
              .sort((a, b) => a.position - b.position)
              .map((battery) => (
                <div className="layout-battery" key={battery.serial}>
                  <div className="layout-battery-main">
                    <input
                      className="input"
                      type="number"
                      min={1}
                      aria-label={`Position of ${battery.serial}`}
                      value={battery.position}
                      disabled={disabled}
                      onChange={(event) => patchBattery(section.id, battery.serial, { position: Math.max(1, Math.trunc(Number(event.target.value) || 1)) })}
                    />
                    <input
                      className="input"
                      aria-label={`Label of ${battery.serial}`}
                      value={battery.label}
                      disabled={disabled}
                      maxLength={80}
                      onChange={(event) => patchBattery(section.id, battery.serial, { label: event.target.value })}
                    />
                  </div>
                  <div className="layout-battery-meta">
                    <span className="serial">{battery.serial}</span>
                    {sectionSelect(battery.serial, section.id)}
                    <label className="checkbox small" title="The battery's position inside the section is confirmed on site">
                      <input
                        type="checkbox"
                        checked={battery.position_confirmed}
                        disabled={disabled}
                        onChange={(event) => patchBattery(section.id, battery.serial, { position_confirmed: event.target.checked })}
                      />
                      Confirmed
                    </label>
                  </div>
                </div>
              ))}
          </div>
        ))}
      </div>
      {layout.unassigned.length > 0 && (
        <div className="layout-section">
          <header>
            <strong>Unassigned batteries</strong>
          </header>
          <p className="muted small">These exports carry no SmartLogger serial. Assign each battery to a section before running the analysis.</p>
          {layout.unassigned.map((serial) => (
            <div className="row between" key={serial} style={{ padding: "0.3rem 0" }}>
              <span className="mono">{serial}</span>
              {sectionSelect(serial, null)}
            </div>
          ))}
        </div>
      )}
      {!disabled && (
        <div>
          <button type="button" className="btn small" onClick={addSection}>
            + Add section
          </button>
        </div>
      )}
    </div>
  );
}
